/**
 * Types + fetchery dla pluginu `c_procedures` rozszerzajacego shell `c_user_accounts`
 * (procedury dostepne dla usera - lista + podglad + plik + akceptacja/odrzucenie).
 *
 * Backend (IRESTAPI, routing po NAZWIE METODY - `IAPI_` strip):
 *   - GET  /IRESTAPI/v1/C_PROCEDURES_CONTROLLER/userProcedures             (caly zbior + truncated)
 *   - GET  /IRESTAPI/v1/C_PROCEDURES_CONTROLLER/userProcedureDetail/{id}   (szczegoly + oznacz otwarta)
 *   - POST /IRESTAPI/v1/C_PROCEDURES_CONTROLLER/userProcedureDecision/{id} (accept/reject)
 *
 * Plik procedury pobierany jest bezposrednio z backendu przez zaszyfrowany link
 * `file_href` (`/IGETFILE/{blob}`) generowany po stronie serwera przez
 * `C_PROCEDURES_CONTROLLER::getUserProcedureFileUrl`. Wzorzec spojny z c_facture
 * (pdf_href) i c_electronic_signs (preview_href) - id procedury jest w tamper-proof
 * payloadzie, ownership (grupy usera vs public_for) sprawdzany po stronie backendu
 * w handlerze `userProcedureFileHandler`. Browser nawiguje bezposrednio (bez proxy
 * z Bearer - IGETFILE dziala na PHP session cookie).
 *
 * Lista jest client-side gridem (TanStack Table) - backend zwraca caly bounded zbior.
 *
 * Frontend route handlery (proxy + Bearer) w `app/api/iiapi/c-procedures/`.
 */

/* Statusy akceptacji (parytet z define.php: PROCEDURE_ACCEPT=1, PROCEDURE_REJECT=2).
   -1 = procedura jeszcze nieotwarta przez usera (brak wiersza). */
export const PROCEDURE_STATUS_NOT_OPENED = -1;
export const PROCEDURE_ACCEPT = 1;
export const PROCEDURE_REJECT = 2;

export type UserProcedure = {
  id: number;
  name: string;
  /** Skrocony opis (do 350 znakow). */
  description: string;
  /** Nazwa procedury nadrzednej ('' gdy brak). */
  parent_name: string;
  add_date: string;
  /** Termin akceptacji (add_date + N dni). */
  deadline: string;
  /** Czy wymagana (1) czy opcjonalna (0). */
  required: number;
  /** Status akceptacji usera (-1 nieotwarta, 1 zaakceptowana, 2 odrzucona). */
  status: number;
  /**
   * Absolutny zaszyfrowany URL pobrania pliku procedury (`/IGETFILE/{blob}`).
   * `null` gdy procedura nie ma pliku. Ownership sprawdzany server-side.
   */
  file_href: string | null;
};

export type UserProcedureDetail = {
  id: number;
  name: string;
  /** Pelna tresc procedury (HTML/tekst). */
  description: string;
  parent_name: string;
  required: number;
  status: number;
  /** Absolutny zaszyfrowany URL pobrania pliku (`null` gdy brak). */
  file_href: string | null;
  file_name: string | null;
};

/**
 * Pobiera liste procedur usera z route handler proxy.
 *
 * @throws Error gdy fetch fail / response !ok.
 */
export async function fetchUserProcedures(): Promise<{
  procedures: UserProcedure[];
  truncated: boolean;
}> {
  const res = await fetch("/api/iiapi/c-procedures/procedures", { cache: "no-store" });
  if (!res.ok) throw new Error(`Fetch procedures failed: HTTP ${res.status}`);
  const body = (await res.json()) as
    | { ok: true; data: { procedures: UserProcedure[]; truncated: boolean } }
    | { ok: false; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error("message" in body ? (body.message ?? "Fetch failed") : "Invalid response");
  }
  return {
    procedures: body.data?.procedures ?? [],
    truncated: body.data?.truncated ?? false,
  };
}

/**
 * Pobiera szczegoly procedury (oznacza jako otwarta po stronie backendu).
 *
 * @throws Error gdy fetch fail / response !ok.
 */
export async function fetchProcedureDetail(id: number): Promise<UserProcedureDetail> {
  const res = await fetch(`/api/iiapi/c-procedures/procedure/${id}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`Fetch procedure detail failed: HTTP ${res.status}`);
  const body = (await res.json()) as
    | { ok: true; data: UserProcedureDetail }
    | { ok: false; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error("message" in body ? (body.message ?? "Fetch failed") : "Invalid response");
  }
  return body.data;
}

/**
 * Wysyla decyzje (akceptacja/odrzucenie) procedury. Zwraca nowy status.
 *
 * @throws Error gdy fetch fail / response !ok.
 */
export async function submitProcedureDecision(
  id: number,
  decision: "accept" | "reject",
): Promise<number> {
  const res = await fetch(`/api/iiapi/c-procedures/procedure/${id}/decision`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ decision }),
  });
  const body = (await res.json()) as
    | { ok: true; data: { status: number } }
    | { ok: false; message?: string };
  if (!res.ok || !("ok" in body) || body.ok !== true) {
    throw new Error("message" in body ? (body.message ?? "Decision failed") : "Invalid response");
  }
  return body.data?.status ?? PROCEDURE_STATUS_NOT_OPENED;
}

/* ─── Bramka procedur (banner / blokada konta) ─── */

export type ProcedureGatePending = {
  name: string;
  /** Dni do konca terminu akceptacji (0 gdy po terminie). */
  days_left: number;
  /** Czy procedura jest juz po terminie. */
  overdue: boolean;
};

export type ProcedureGate = {
  /** `true` gdy istnieje wymagana procedura po terminie -> twarda blokada konta. */
  block: boolean;
  /** action_id zakladki procedur (deep-link w shellu konta). */
  action: string;
  /** Wszystkie wymagane niezaakceptowane procedury (w terminie + po terminie). */
  pending: ProcedureGatePending[];
};

/**
 * Pobiera status bramki procedur dla zalogowanego usera.
 * Zwraca `{block:false, pending:[]}` gdy brak procedur / blad (fail-soft -
 * bramka nie moze zablokowac konta z powodu bledu sieci).
 */
export async function fetchProcedureGate(): Promise<ProcedureGate> {
  const fallback: ProcedureGate = { block: false, action: "get-procedures", pending: [] };
  try {
    const res = await fetch("/api/iiapi/c-procedures/gate", { cache: "no-store" });
    if (!res.ok) return fallback;
    const body = (await res.json()) as
      | { ok: true; data: ProcedureGate }
      | { ok: false };
    if (!("ok" in body) || body.ok !== true) return fallback;
    return {
      block: body.data?.block ?? false,
      action: body.data?.action ?? "get-procedures",
      pending: body.data?.pending ?? [],
    };
  } catch {
    return fallback;
  }
}
